import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { verifyLogin } from "../../api/api-autenticacion";
import {
  STRING_ACCESS_TOKEN_ULTIMA_MILLA_V3,
  STRING_ID_EMPRESA_ULTIMA_MILLA_V3,
  STRING_LANGUAGE_ULTIMA_MILLA,
  STRING_SIDEBAR_EXPANDED_UMV3,
  STRING_ID_ROL_USUARIO_WEB,
  STRING_ID_TIPO_USUARIO_WEB,
  STRING_ID_USUARIO_WEB,
} from "../../utils/const/string-const";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { Eye, EyeOff } from "lucide-react";
import { VERSION_TMS } from "../../utils/const/version-const";
// import { sharedNextPage } from "../../utils/shared-functions";

export default function Login() {
  const { t } = useTranslation();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("es");
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();

  const languages = [
    { value: "en", label: "English", flag: "🇺🇸" },
    { value: "es", label: "Español", flag: "🇪🇸" },
    { value: "pt", label: "Português", flag: "🇧🇷" },
  ];

  useEffect(() => {
    const language = localStorage.getItem(STRING_LANGUAGE_ULTIMA_MILLA);
    if (language) {
      setSelectedLanguage(language);
      i18n.changeLanguage(language);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isLanguageDropdownOpen &&
        !event.target.closest(".language-dropdown")
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  const submit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      const response = await verifyLogin(usuario, password);
      setLoading(false);
      // console.log('verifyLogin response');
      console.log(response);
      if (response.error === false) {
        localStorage.setItem(
          STRING_ACCESS_TOKEN_ULTIMA_MILLA_V3,
          response.body,
        );
        localStorage.setItem(STRING_SIDEBAR_EXPANDED_UMV3, true);
        localStorage.setItem(
          STRING_ID_EMPRESA_ULTIMA_MILLA_V3,
          response.idEmpresa,
        );
        localStorage.setItem(
          STRING_ID_ROL_USUARIO_WEB,
          response.idRolUsuarioWeb,
        );
        localStorage.setItem(
          STRING_ID_TIPO_USUARIO_WEB,
          response.idTipoUsuarioWeb,
        );
        localStorage.setItem(STRING_ID_USUARIO_WEB, response.idUsuario);
        i18n.changeLanguage(selectedLanguage);
        history.push(`/dashboard`);
      } else {
        alert(t("alert_usuario_o_contrasena_incorrecto"));
      }
    } catch (err) {
      console.log("submit err");
      console.log(err);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        {/* Geometric patterns */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gray-100 rounded-full opacity-40 blur-3xl"></div>

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md relative z-10">
        <form onSubmit={submit}>
          {/* Logo/Brand Section */}
          <div className="text-center mb-8">
            <div className="inline-block">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-2 tracking-tight">
                DataTerra
              </h1>
              <p className="text-gray-600 text-sm font-medium">
                {t("label_plataforma_de_ultima_milla")}
              </p>
            </div>
          </div>

          {/* Login Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 sm:p-10 hover:shadow-2xl transition-shadow duration-300">
            <div className="mb-8">
              <div className="flex justify-between items-start mb-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t("label_iniciar_sesion")}
                </h2>

                {/* Language Dropdown */}
                <div className="relative language-dropdown">
                  <button
                    type="button"
                    onClick={() =>
                      setIsLanguageDropdownOpen(!isLanguageDropdownOpen)
                    }
                    className="flex items-center space-x-1 px-2 py-1 rounded-lg hover:bg-gray-100 transition"
                  >
                    <label>
                      <span className="text-lg">
                        {
                          languages.find(
                            (lang) => lang.value === selectedLanguage,
                          )?.flag
                        }
                      </span>{" "}
                      {
                        languages.find(
                          (lang) => lang.value === selectedLanguage,
                        )?.label
                      }
                    </label>
                    <svg
                      className={`w-3 h-3 text-gray-500 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isLanguageDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                      {languages.map((language) => (
                        <button
                          type="button"
                          key={language.value}
                          onClick={() => {
                            setSelectedLanguage(language.value);
                            setIsLanguageDropdownOpen(false);
                            i18n.changeLanguage(language.value);
                            localStorage.setItem(
                              STRING_LANGUAGE_ULTIMA_MILLA,
                              language.value,
                            );
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 transition ${
                            selectedLanguage === language.value
                              ? "bg-blue-50 text-blue-600"
                              : "text-gray-700"
                          }`}
                        >
                          <span className="text-lg">{language.flag}</span>
                          <span className="font-medium text-sm">
                            {language.label}
                          </span>
                          {selectedLanguage === language.value && (
                            <svg
                              className="w-4 h-4 ml-auto text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-gray-600 text-sm">
                {t("label_ingresa_tus_credenciales_para_continuar")}
              </p>
            </div>

            <div className="space-y-6">
              {/* Usuario Field */}
              <div>
                <label
                  htmlFor="usuario_login"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("label_usuario")}
                </label>
                <input
                  id="usuario_login"
                  type="text"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 placeholder-gray-400 hover:border-gray-400"
                  placeholder={t("label_ingresa_tu_usuario")}
                  required
                  disabled={loading}
                />
              </div>

              {/* Contraseña Field */}
              <div>
                <label
                  htmlFor="password_login"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("label_contrasena")}
                </label>
                <div className="relative">
                  <input
                    id="password_login"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 placeholder-gray-400 hover:border-gray-400"
                    placeholder={t("label_ingresa_tu_contrasena")}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                    disabled={loading}
                    aria-label={
                      showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                    }
                  >
                    <div className="relative w-5 h-5">
                      <Eye
                        className={`absolute inset-0 transition-all duration-300 ${
                          showPassword
                            ? "opacity-0 scale-75 rotate-90"
                            : "opacity-100 scale-100 rotate-0"
                        }`}
                        size={20}
                      />
                      <EyeOff
                        className={`absolute inset-0 transition-all duration-300 ${
                          showPassword
                            ? "opacity-100 scale-100 rotate-0"
                            : "opacity-0 scale-75 -rotate-90"
                        }`}
                        size={20}
                      />
                    </div>
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3.5 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="ml-2">
                        {t("label_cargando") || "Cargando..."}
                      </span>
                    </>
                  ) : (
                    <span>{t("label_ingresar")}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-center text-xs text-gray-500">
                version {VERSION_TMS}
              </p>
            </div>
          </div>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => history.push("/")}
              className="text-gray-600 hover:text-blue-600 text-sm font-medium transition duration-200 inline-flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t("label_volver_al_inicio")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
